// CFOPage.js — FX Capital India | CFO Performance View
// Live rates from fx.fxcapitalindia.in directly — no DLL dependency
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import CONFIG from '../config';

const BILL   = CONFIG.API + '/api/bills';
const FX_API = 'https://fx.fxcapitalindia.in/api';

// ── Helpers ────────────────────────────────────────────────────────────────
const fmtFC = (v) => {
  const n = Number(v);
  if (isNaN(n) || n === 0) return '—';
  if (n >= 10000000) return (n / 10000000).toFixed(2) + ' Cr';
  if (n >= 100000)   return (n / 100000).toFixed(2) + ' L';
  return n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

const fmtINR = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '—';
  const abs  = Math.abs(n);
  const sign = n < 0 ? '−' : '+';
  if (abs >= 10000000) return sign + '₹' + (abs / 10000000).toFixed(2) + ' Cr';
  if (abs >= 100000)   return sign + '₹' + (abs / 100000).toFixed(2) + ' L';
  return sign + '₹' + abs.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

const fmtR = (v, d = 4) => v == null || v === 0 ? '—' : Number(v).toFixed(d);
const glCol = (v) => Number(v) >= 0 ? '#00b67a' : '#e8315a';

const gradeColor = (g) => ({
  'Excellent':    '#00b67a',
  'Strong':       '#16c47f',
  'Good':         '#f59e0b',
  'Fair':         '#e87a00',
  'Below Market': '#e8315a',
}[g] || '#8896b0');

const gradeStars = (g) => ({
  'Excellent':    5,
  'Strong':       4,
  'Good':         3,
  'Fair':         2,
  'Below Market': 1,
}[g] || 0);

const toIso = (d) => d.toISOString().slice(0, 10);

const PAIRS = ['USD', 'EUR', 'GBP', 'AED'];

const CCY_COLORS = {
  USD: { bg: '#1d4ed8', light: '#eff6ff', border: '#bfdbfe' },
  EUR: { bg: '#5b21b6', light: '#f5f3ff', border: '#ddd6fe' },
  GBP: { bg: '#0f766e', light: '#f0fdfa', border: '#99f6e4' },
  AED: { bg: '#b45309', light: '#fffbeb', border: '#fde68a' },
};

// ── Styles ─────────────────────────────────────────────────────────────────
const S = {
  page: {
    background: '#f0f4fb',
    minHeight: '100vh',
    paddingBottom: 80,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
    color: '#1a2540',
  },
  topBar: {
    background: '#0a0f1c',
    padding: '10px 14px 8px',
    borderBottom: '2px solid #c9a84c',
    position: 'sticky', top: 0, zIndex: 100,
  },
  topBarRow: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: 8,
  },
  logoBox: {
    width: 30, height: 30, background: '#c9a84c',
    borderRadius: 6, display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontWeight: 900, fontSize: 12,
    color: '#0a0f1c', marginRight: 8, flexShrink: 0,
  },
  ratePill: {
    flex: '0 0 auto', paddingRight: 16,
    borderRight: '1px solid rgba(255,255,255,0.1)',
    marginRight: 16,
  },
  card: {
    background: '#fff',
    borderRadius: 14,
    border: '1px solid #e4e8f0',
    overflow: 'hidden',
    marginBottom: 12,
  },
  cardHead: (color = '#0a0f1c') => ({
    background: color,
    padding: '10px 14px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  }),
};

// ══════════════════════════════════════════════════════════════════════════
// LIVE RATE STRIP
// ══════════════════════════════════════════════════════════════════════════
const RateStrip = ({ rates }) => (
  <div style={{ display: 'flex', overflowX: 'auto', paddingBottom: 2, scrollbarWidth: 'none' }}>
    {PAIRS.map(ccy => {
      const r  = rates?.[ccy + 'INR'];
      const up = r && Number(r.change) >= 0;
      return (
        <div key={ccy} style={S.ratePill}>
          <div style={{ fontSize: 9, color: '#8896b0', letterSpacing: 1 }}>{ccy}</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: r ? '#c9a84c' : '#8896b0', fontVariantNumeric: 'tabular-nums' }}>
            {r ? Number(r.ltp).toFixed(4) : '—'}
          </div>
          <div style={{ fontSize: 9, color: r ? (up ? '#00b67a' : '#e8315a') : '#8896b0' }}>
            {r ? (up ? '▲ ' : '▼ ') + Math.abs(r.change || 0).toFixed(4) : 'offline'}
          </div>
        </div>
      );
    })}
  </div>
);

// ══════════════════════════════════════════════════════════════════════════
// PORTFOLIO SUMMARY STRIP
// ══════════════════════════════════════════════════════════════════════════
const PortfolioStrip = ({ kpis2, rates }) => {
  if (!kpis2) return (
    <div style={{ ...S.card, margin: '12px 12px 0', padding: 16 }}>
      <div style={{ fontSize: 12, color: '#8896b0', textAlign: 'center' }}>
        Portfolio data loads after server update — rates are live above ✅
      </div>
    </div>
  );

  const hedgeMap = kpis2.hedgeRatio || {};
  let totalExport = 0, totalFwd = 0, totalPcfc = 0, totalImport = 0;
  PAIRS.forEach(ccy => {
    const h = hedgeMap[ccy + 'INR'];
    if (h) {
      totalExport += Number(h.exportFc  || 0);
      totalFwd    += Number(h.forwardFc || 0);
      totalPcfc   += Number(h.pcfcFc    || 0);
      totalImport += Number(h.importFc  || 0);
    }
  });

  const netPos      = Math.max(totalExport - totalImport, 0);
  const totalHedged = Math.min(totalFwd + totalPcfc + totalImport, netPos);
  const hedgePct    = netPos > 0 ? Math.round(totalHedged / netPos * 100) : 0;
  const totalMtm    = kpis2.portfolioMtmInr || 0;

  return (
    <div style={{ ...S.card, margin: '12px 12px 0' }}>
      <div style={S.cardHead()}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.8 }}>
          Portfolio Snapshot
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#00b67a' }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#00b67a' }} />
          Live
        </div>
      </div>
      <div style={{ padding: '12px 14px' }}>
        <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1, background: '#f0f4fb', borderRadius: 10, padding: '10px 12px' }}>
            <div style={{ fontSize: 9, color: '#8896b0', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>
              Total Exposure
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#1a2540', fontVariantNumeric: 'tabular-nums' }}>
              {fmtINR(kpis2.totalOpenExposureInr).replace('+', '')}
            </div>
          </div>
          <div style={{
            flex: 1, background: totalMtm >= 0 ? '#e8fff5' : '#fff0f3',
            borderRadius: 10, padding: '10px 12px',
            border: `1px solid ${totalMtm >= 0 ? '#a7f3d0' : '#fecdd3'}`,
          }}>
            <div style={{ fontSize: 9, color: '#8896b0', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 3 }}>
              Portfolio MTM
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: glCol(totalMtm), fontVariantNumeric: 'tabular-nums' }}>
              {fmtINR(totalMtm)}
            </div>
          </div>
        </div>
        <div style={{ marginBottom: 4 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 11, color: '#8896b0' }}>Overall Hedge Ratio</span>
            <span style={{
              fontSize: 13, fontWeight: 800,
              color: hedgePct >= 70 ? '#00b67a' : hedgePct >= 40 ? '#f59e0b' : '#e8315a',
            }}>
              {hedgePct}%
            </span>
          </div>
          <div style={{ height: 8, background: '#e4e8f0', borderRadius: 4, overflow: 'hidden', display: 'flex' }}>
            <div style={{ width: `${Math.min(netPos > 0 ? totalFwd / netPos * 100 : 0, 100)}%`, background: '#1d4ed8' }} />
            <div style={{ width: `${Math.min(netPos > 0 ? totalPcfc / netPos * 100 : 0, 100)}%`, background: '#7c3aed' }} />
            <div style={{ width: `${Math.min(netPos > 0 ? totalImport / netPos * 100 : 0, 100)}%`, background: '#00b67a' }} />
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 5 }}>
            {[
              { color: '#1d4ed8', label: 'Fwd',  val: fmtFC(totalFwd)  },
              { color: '#7c3aed', label: 'PCFC', val: fmtFC(totalPcfc) },
              { color: '#8896b0', label: 'Open', val: fmtFC(Math.max(netPos - totalHedged, 0)) },
            ].map(item => (
              <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10 }}>
                <div style={{ width: 8, height: 8, borderRadius: 2, background: item.color, flexShrink: 0 }} />
                <span style={{ color: '#8896b0' }}>{item.label}</span>
                <span style={{ color: '#1a2540', fontWeight: 600 }}>{item.val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// CURRENCY CARD
// ══════════════════════════════════════════════════════════════════════════
const CurrencyCard = ({ ccy, data, hedge, liveRate }) => {
  const colors  = CCY_COLORS[ccy] || CCY_COLORS.USD;
  const fcOpen  = Number(data?.fcOpen      || 0);
  const cost    = Number(data?.avgCostRate || 0);
  const live    = Number(liveRate?.ltp     || 0);
  const mtm     = Number(data?.mtmInr      || 0);
  const fwdFc   = Number(hedge?.forwardFc  || 0);
  const pcfcFc  = Number(hedge?.pcfcFc     || 0);
  const importFc= Number(hedge?.importFc   || 0);
  const netPos  = Number(hedge?.netPosition|| 0);
  const hedgePct= Number(hedge?.hedgePct   || 0);
  const up      = live > 0 && Number(liveRate?.change || 0) >= 0;

  return (
    <div style={{
      width: 200, flexShrink: 0, background: '#fff',
      borderRadius: 14, border: `1px solid ${colors.border}`,
      overflow: 'hidden', marginRight: 10,
    }}>
      <div style={{ background: colors.bg, padding: '10px 12px 8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 900, color: '#fff', letterSpacing: 1 }}>{ccy}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: 'rgba(255,255,255,0.9)', fontVariantNumeric: 'tabular-nums' }}>
              {fmtFC(fcOpen)}
            </div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)' }}>FC Open</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#c9a84c', fontVariantNumeric: 'tabular-nums' }}>
              {live > 0 ? live.toFixed(4) : '—'}
            </div>
            <div style={{ fontSize: 9, color: live > 0 ? (up ? '#4ade80' : '#f87171') : '#8896b0' }}>
              {live > 0 ? (up ? '▲ ' : '▼ ') + Math.abs(liveRate?.change || 0).toFixed(4) : 'offline'}
            </div>
            <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.4)' }}>Live Rate</div>
          </div>
        </div>
      </div>
      <div style={{ padding: '10px 12px' }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          background: colors.light, borderRadius: 8, padding: '6px 10px', marginBottom: 8,
        }}>
          <div>
            <div style={{ fontSize: 8, color: '#8896b0' }}>COST RATE</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1a2540', fontVariantNumeric: 'tabular-nums' }}>
              {fmtR(cost)}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 8, color: '#8896b0' }}>MTM</div>
            <div style={{ fontSize: 13, fontWeight: 800, color: glCol(mtm), fontVariantNumeric: 'tabular-nums' }}>
              {live > 0 ? fmtINR(mtm) : '—'}
            </div>
          </div>
        </div>
        <div style={{ marginBottom: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 9, color: '#8896b0', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Hedge Ratio
            </span>
            <span style={{
              fontSize: 12, fontWeight: 800,
              color: hedgePct >= 70 ? '#00b67a' : hedgePct >= 40 ? '#f59e0b' : '#e8315a',
            }}>
              {hedgePct}%
            </span>
          </div>
          <div style={{ height: 8, background: '#f0f4fb', borderRadius: 4, overflow: 'hidden', display: 'flex', marginBottom: 6 }}>
            <div style={{ width: `${netPos > 0 ? Math.min(fwdFc / netPos * 100, 100) : 0}%`, background: '#1d4ed8' }} />
            <div style={{ width: `${netPos > 0 ? Math.min(pcfcFc / netPos * 100, 100) : 0}%`, background: '#7c3aed' }} />
            <div style={{ width: `${netPos > 0 ? Math.min(importFc / netPos * 100, 100) : 0}%`, background: '#00b67a' }} />
          </div>
          {[
            { label: 'Forward', val: fwdFc,    color: '#1d4ed8' },
            { label: 'PCFC',    val: pcfcFc,   color: '#7c3aed' },
            { label: 'Import',  val: importFc, color: '#00b67a' },
          ].map(item => (
            <div key={item.label} style={{
              display: 'flex', justifyContent: 'space-between',
              alignItems: 'center', marginBottom: 3,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div style={{ width: 6, height: 6, borderRadius: 2, background: item.color, flexShrink: 0 }} />
                <span style={{ fontSize: 10, color: '#8896b0' }}>{item.label}</span>
              </div>
              <span style={{ fontSize: 10, fontWeight: 600, color: item.val > 0 ? '#1a2540' : '#c8d0e0' }}>
                {item.val > 0 ? fmtFC(item.val) : '—'}
              </span>
            </div>
          ))}
        </div>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          background: '#f0f4fb', borderRadius: 6, padding: '5px 8px',
          borderTop: '1px solid #e4e8f0',
        }}>
          <span style={{ fontSize: 10, color: '#8896b0' }}>Open (Unhedged)</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: hedge?.unhedged > 0 ? '#e8315a' : '#00b67a' }}>
            {fmtFC(hedge?.unhedged || 0)}
          </span>
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// FORWARDS + PCFC SUMMARY CARDS
// ══════════════════════════════════════════════════════════════════════════
const InstrumentCards = ({ forwards, pcfc }) => {
  const fwdList  = forwards || [];
  const pcfcList = pcfc     || [];

  const totalFwdFc  = fwdList.reduce((s, f) => s + Number(f.fcAvailable  || 0), 0);
  const totalFwdMtm = fwdList.reduce((s, f) => s + Number(f.mtmInr       || 0), 0);
  const avgFwdRate  = fwdList.length > 0
    ? fwdList.reduce((s, f) => s + Number(f.bookedRate || 0), 0) / fwdList.length : 0;
  const sortedFwd   = [...fwdList].sort((a, b) => a.daysToExpiry - b.daysToExpiry);
  const nearestFwd  = sortedFwd[0];

  const totalPcfcFc  = pcfcList.reduce((s, p) => s + Number(p.fcAmount     || 0), 0);
  const totalPcfcMtm = pcfcList.reduce((s, p) => s + Number(p.mtmInr       || 0), 0);
  const avgDrawRate  = pcfcList.length > 0
    ? pcfcList.reduce((s, p) => s + Number(p.drawdownRate || 0), 0) / pcfcList.length : 0;
  const urgentPcfc   = pcfcList.filter(p => p.alertLevel === 'URGENT' || p.alertLevel === 'WARNING');

  return (
    <div style={{ display: 'flex', gap: 10, padding: '0 12px', marginBottom: 12 }}>
      <div style={{ ...S.card, flex: 1, margin: 0 }}>
        <div style={S.cardHead('#0f172a')}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#93c5fd', textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Forwards
          </span>
          <span style={{ fontSize: 10, color: '#8896b0' }}>{fwdList.length} contracts</span>
        </div>
        <div style={{ padding: '10px 12px' }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#1a2540', marginBottom: 2, fontVariantNumeric: 'tabular-nums' }}>
            {fmtFC(totalFwdFc)}
          </div>
          <div style={{ fontSize: 9, color: '#8896b0', marginBottom: 8 }}>FC Covered</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 10, color: '#8896b0' }}>Avg Rate</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8', fontVariantNumeric: 'tabular-nums' }}>
              {fmtR(avgFwdRate)}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 10, color: '#8896b0' }}>MTM</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: glCol(totalFwdMtm), fontVariantNumeric: 'tabular-nums' }}>
              {fmtINR(totalFwdMtm)}
            </span>
          </div>
          {nearestFwd && (
            <div style={{
              background: nearestFwd.daysToExpiry <= 14 ? '#fff0f3' : '#eff6ff',
              borderRadius: 6, padding: '4px 8px', fontSize: 10,
              color: nearestFwd.daysToExpiry <= 14 ? '#e8315a' : '#1d4ed8',
              fontWeight: 600,
            }}>
              {nearestFwd.daysToExpiry <= 0
                ? '⚠ Expired'
                : `Next exp: ${nearestFwd.windowEnd} (${nearestFwd.daysToExpiry}d)`}
            </div>
          )}
        </div>
      </div>

      <div style={{ ...S.card, flex: 1, margin: 0 }}>
        <div style={S.cardHead('#1e1b4b')}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#c4b5fd', textTransform: 'uppercase', letterSpacing: 0.6 }}>
            PCFC
          </span>
          <span style={{ fontSize: 10, color: '#8896b0' }}>{pcfcList.length} tranches</span>
        </div>
        <div style={{ padding: '10px 12px' }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#1a2540', marginBottom: 2, fontVariantNumeric: 'tabular-nums' }}>
            {fmtFC(totalPcfcFc)}
          </div>
          <div style={{ fontSize: 9, color: '#8896b0', marginBottom: 8 }}>FC Outstanding</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 10, color: '#8896b0' }}>Avg Draw Rate</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', fontVariantNumeric: 'tabular-nums' }}>
              {fmtR(avgDrawRate)}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 10, color: '#8896b0' }}>MTM</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: glCol(totalPcfcMtm), fontVariantNumeric: 'tabular-nums' }}>
              {fmtINR(totalPcfcMtm)}
            </span>
          </div>
          {urgentPcfc.length > 0 && (
            <div style={{
              background: urgentPcfc[0].alertLevel === 'URGENT' ? '#fff0f3' : '#fffbeb',
              borderRadius: 6, padding: '4px 8px', fontSize: 10,
              color: urgentPcfc[0].alertLevel === 'URGENT' ? '#e8315a' : '#f59e0b',
              fontWeight: 600,
            }}>
              ⚠ {urgentPcfc[0].trancheId} — {urgentPcfc[0].daysRemaining}d left
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// PERFORMANCE SECTION
// ══════════════════════════════════════════════════════════════════════════
const PerformanceSection = ({ token }) => {
  const today = new Date();
  const [from,    setFrom]    = useState(toIso(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [to,      setTo]      = useState(toIso(today));
  const [ccy,     setCcy]     = useState('USD');
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);

  const hdr = { headers: { authorization: 'Bearer ' + token } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BILL}/performance/summary?pair=${ccy}&from=${from}&to=${to}`, hdr);
      setData(res.data);
    } catch { setData(null); }
    setLoading(false);
  }, [from, to, ccy, token]);

  useEffect(() => { load(); }, [from, to, ccy]);

  const stars  = data ? gradeStars(data.perfGrade) : 0;
  const gc     = data ? gradeColor(data.perfGrade) : '#8896b0';
  const glCost = data?.wtdAvgRealisedRate && data?.wtdAvgCostRate && data?.totalFc
    ? (data.wtdAvgRealisedRate - data.wtdAvgCostRate) * data.totalFc : null;
  const glMkt  = data?.wtdAvgRealisedRate && data?.marketSma && data?.totalFc
    ? (data.wtdAvgRealisedRate - data.marketSma) * data.totalFc : null;

  const low   = data?.marketLow;
  const high  = data?.marketHigh;
  const range = high && low ? high - low : 0;
  const pct   = (v) => range > 0 && v != null
    ? Math.min(Math.max((v - low) / range * 100, 2), 98) : null;

  return (
    <div style={{ ...S.card, margin: '0 12px 12px' }}>
      <div style={S.cardHead('#0a1628')}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.8 }}>
          Performance Benchmarking
        </span>
        {data?.perfGrade && (
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 10px',
            borderRadius: 12, background: gc + '33', color: gc,
          }}>
            {data.perfGrade}
          </span>
        )}
      </div>

      <div style={{
        display: 'flex', gap: 6, padding: '10px 12px 8px',
        borderBottom: '1px solid #f0f4fb', flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {PAIRS.map(c => (
            <button key={c} onClick={() => setCcy(c)} style={{
              padding: '4px 10px', borderRadius: 16, fontSize: 11,
              fontWeight: 600, cursor: 'pointer', border: 'none',
              background: ccy === c ? CCY_COLORS[c].bg : '#f0f4fb',
              color:      ccy === c ? '#fff' : '#8896b0',
            }}>{c}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flex: 1 }}>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            style={{
              flex: 1, minWidth: 110, padding: '4px 8px', borderRadius: 8,
              border: '1px solid #e4e8f0', fontSize: 11, color: '#1a2540', background: '#f0f4fb',
            }} />
          <span style={{ fontSize: 11, color: '#8896b0' }}>→</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            style={{
              flex: 1, minWidth: 110, padding: '4px 8px', borderRadius: 8,
              border: '1px solid #e4e8f0', fontSize: 11, color: '#1a2540', background: '#f0f4fb',
            }} />
        </div>
      </div>

      {loading && (
        <div style={{ padding: 20, textAlign: 'center', color: '#8896b0', fontSize: 13 }}>Loading…</div>
      )}

      {!loading && !data && (
        <div style={{ padding: 16, color: '#8896b0', fontSize: 13 }}>
          No settled bills in this period for {ccy}
        </div>
      )}

      {!loading && data && data.billsCount > 0 && (
        <div style={{ padding: '10px 12px' }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
            {[
              { label: 'Bills',      val: data.billsCount,    color: '#1a2540' },
              { label: 'FC Settled', val: fmtFC(data.totalFc), color: '#1a2540' },
            ].map(chip => (
              <div key={chip.label} style={{
                flex: 1, background: '#f0f4fb', borderRadius: 8,
                padding: '6px 10px', textAlign: 'center',
              }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: chip.color }}>{chip.val}</div>
                <div style={{ fontSize: 9, color: '#8896b0', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {chip.label}
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 12 }}>
            {[
              { label: 'Avg Cost Rate',     val: fmtR(data.wtdAvgCostRate),     color: '#1d4ed8' },
              { label: 'Avg Realised Rate', val: fmtR(data.wtdAvgRealisedRate), color: '#00b67a' },
              { label: 'Market SMA',        val: data.marketSma ? fmtR(data.marketSma) : 'N/A', color: '#f59e0b' },
            ].map(item => (
              <div key={item.label} style={{
                background: '#f0f4fb', borderRadius: 8, padding: '8px 6px', textAlign: 'center',
              }}>
                <div style={{ fontSize: 9, color: '#8896b0', marginBottom: 3, lineHeight: 1.3 }}>
                  {item.label}
                </div>
                <div style={{ fontSize: 14, fontWeight: 800, color: item.color, fontVariantNumeric: 'tabular-nums' }}>
                  {item.val}
                </div>
              </div>
            ))}
          </div>

          <div style={{
            background: '#fffbeb', borderRadius: 8, padding: '8px 12px',
            marginBottom: 12, border: '1px solid #fde68a',
          }}>
            <div style={{ fontSize: 9, color: '#8896b0', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
              Market Range — {ccy}INR
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              {[
                { label: 'High', val: data.marketHigh, color: '#00b67a' },
                { label: 'SMA',  val: data.marketSma,  color: '#f59e0b' },
                { label: 'Low',  val: data.marketLow,  color: '#e8315a' },
              ].map(item => (
                <div key={item.label} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 9, color: '#8896b0' }}>{item.label}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: item.color, fontVariantNumeric: 'tabular-nums' }}>
                    {item.val ? fmtR(item.val, 2) : 'N/A'}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <div style={{
              flex: 1, borderRadius: 10, padding: '10px 12px', textAlign: 'center',
              background: glCost != null ? (glCost >= 0 ? '#e8fff5' : '#fff0f3') : '#f0f4fb',
              border: `1px solid ${glCost != null ? (glCost >= 0 ? '#a7f3d0' : '#fecdd3') : '#e4e8f0'}`,
            }}>
              <div style={{ fontSize: 9, color: '#8896b0', marginBottom: 3 }}>G/L vs Cost</div>
              <div style={{ fontSize: 17, fontWeight: 900, color: glCost != null ? glCol(glCost) : '#8896b0', fontVariantNumeric: 'tabular-nums' }}>
                {glCost != null ? fmtINR(glCost) : '—'}
              </div>
            </div>
            <div style={{
              flex: 1, borderRadius: 10, padding: '10px 12px', textAlign: 'center',
              background: glMkt != null ? (glMkt >= 0 ? '#e8fff5' : '#fff0f3') : '#f0f4fb',
              border: `1px solid ${glMkt != null ? (glMkt >= 0 ? '#a7f3d0' : '#fecdd3') : '#e4e8f0'}`,
            }}>
              <div style={{ fontSize: 9, color: '#8896b0', marginBottom: 3 }}>G/L vs Market</div>
              <div style={{ fontSize: 17, fontWeight: 900, color: glMkt != null ? glCol(glMkt) : '#8896b0', fontVariantNumeric: 'tabular-nums' }}>
                {glMkt != null ? fmtINR(glMkt) : 'N/A'}
              </div>
            </div>
          </div>

          {range > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 9, color: '#8896b0', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
                Rate Position — Period Range
              </div>
              <div style={{ position: 'relative', height: 8, background: 'linear-gradient(90deg,#e8315a,#f59e0b 40%,#00b67a 80%,#00d4aa)', borderRadius: 4, margin: '28px 0 20px' }}>
                {[
                  { label: 'Cost',     val: data.wtdAvgCostRate,    color: '#1d4ed8' },
                  { label: 'SMA',      val: data.marketSma,          color: '#f59e0b' },
                  { label: 'Realised', val: data.wtdAvgRealisedRate, color: '#00b67a' },
                ].filter(m => m.val != null && pct(m.val) != null).map((m) => (
                  <div key={m.label} style={{
                    position: 'absolute', left: pct(m.val) + '%',
                    transform: 'translateX(-50%)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                  }}>
                    <div style={{
                      fontSize: 8, fontWeight: 700, color: m.color,
                      marginTop: -22, whiteSpace: 'nowrap',
                      background: '#fff', padding: '1px 4px',
                      borderRadius: 3, border: `1px solid ${m.color}55`,
                    }}>
                      {m.label}
                    </div>
                    <div style={{
                      width: 12, height: 12, borderRadius: '50%',
                      background: m.color, border: '2px solid #fff',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.2)', marginTop: 2,
                    }} />
                    <div style={{ fontSize: 8, color: m.color, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>
                      {fmtR(m.val, 2)}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 8, color: '#8896b0' }}>
                <span>Low {fmtR(low, 2)}</span>
                <span>High {fmtR(high, 2)}</span>
              </div>
            </div>
          )}

          {data.perfGrade && (
            <div style={{
              background: gc + '11', border: `1px solid ${gc}44`,
              borderRadius: 10, padding: '10px 14px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <div style={{ fontSize: 9, color: '#8896b0', marginBottom: 2 }}>PERFORMANCE RANK</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: gc }}>{data.perfGrade}</div>
                {data.perfPosition != null && (
                  <div style={{ fontSize: 10, color: '#8896b0', marginTop: 2 }}>
                    Top {(100 - data.perfPosition).toFixed(0)}% of market range
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 20, letterSpacing: 2 }}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <span key={i} style={{ color: i < stars ? gc : '#e4e8f0' }}>★</span>
                  ))}
                </div>
                {data.perfPosition != null && (
                  <div style={{ fontSize: 10, color: '#8896b0', marginTop: 2 }}>
                    {data.perfPosition.toFixed(0)}th percentile
                  </div>
                )}
              </div>
            </div>
          )}

          {!data.marketHigh && (
            <div style={{
              background: '#fff7ed', border: '1px solid #fed7aa',
              borderRadius: 8, padding: '8px 12px', fontSize: 11,
              color: '#b45309', marginTop: 8,
            }}>
              ⚠ Upload OHLC rate data via Rates tab to see market benchmarks
            </div>
          )}
        </div>
      )}

      {!loading && data && data.billsCount === 0 && (
        <div style={{ padding: 16, color: '#8896b0', fontSize: 13 }}>
          No settled bills for {ccy} in this period
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════
export default function CFOPage({ token }) {
  const [liveRates, setLiveRates] = useState(null);
  const [kpis2,     setKpis2]     = useState(null);
  const [forwards,  setForwards]  = useState([]);
  const [pcfc,      setPcfc]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const intervalRef = useRef(null);

  const hdr = { headers: { authorization: 'Bearer ' + token } };

  // ── Fetch live rates from fx.fxcapitalindia.in directly ─────────────────
  const fetchLive = useCallback(async () => {
    try {
      const res = await axios.get(`${FX_API}/rates/live`);
      if (!res.data || !Array.isArray(res.data)) return;

      const mapped = {};
      res.data.forEach(r => {
        if (!r.currencyPair) return;
        if (!['USDINR', 'EURINR', 'GBPINR', 'AEDINR'].includes(r.currencyPair)) return;
        mapped[r.currencyPair] = {
          pair:      r.currencyPair,
          bid:       r.bid,
          ask:       r.ask,
          ltp:       r.ltp,
          prev:      r.close,
          change:    r.netChange,
          updatedAt: r.updatedAt
        };
      });

      if (Object.keys(mapped).length > 0)
        setLiveRates(mapped);
    } catch {}
  }, []);

  // ── Fetch static data from trm API (forwards + pcfc work with old DLL) ──
  const fetchStatic = useCallback(async () => {
    try {
      const [k2, fwd, pc] = await Promise.all([
        axios.get(`${BILL}/dashboard/kpis2`,               hdr).catch(() => ({ data: null })),
        axios.get(`${BILL}/dashboard/forwards?pair=USDINR`, hdr).catch(() => ({ data: { forwards: [] } })),
        axios.get(`${BILL}/dashboard/pcfc?pair=USDINR`,     hdr).catch(() => ({ data: { pcfc: [] } })),
      ]);
      if (k2.data) setKpis2(k2.data);
      setForwards(fwd.data?.forwards || []);
      setPcfc(pc.data?.pcfc          || []);
    } catch {}
  }, [token]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchLive(), fetchStatic()]);
      setLoading(false);
    })();

    // Live rates every 10 seconds from fx API
    intervalRef.current = setInterval(fetchLive, 10000);
    return () => clearInterval(intervalRef.current);
  }, []);

  return (
    <div style={S.page}>
      {/* Top bar */}
      <div style={S.topBar}>
        <div style={S.topBarRow}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={S.logoBox}>FX</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>FX Capital India</div>
              <div style={{ fontSize: 9, color: '#8896b0' }}>CFO Performance View</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: liveRates ? '#00b67a' : '#e8315a' }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: liveRates ? '#00b67a' : '#e8315a' }} />
            {liveRates ? 'Live' : 'Connecting…'}
          </div>
        </div>
        <RateStrip rates={liveRates} />
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#8896b0', fontSize: 14 }}>
          Loading CFO View…
        </div>
      ) : (
        <>
          <PortfolioStrip kpis2={kpis2} rates={liveRates} />

          <div style={{
            overflowX: 'auto', display: 'flex',
            padding: '12px 12px 4px', scrollbarWidth: 'none',
          }}>
            {PAIRS.map(ccy => {
              const ccyData   = kpis2?.byCurrency?.find(b => b.currency === ccy);
              const hedgeData = kpis2?.hedgeRatio?.[ccy + 'INR'];
              const liveRate  = liveRates?.[ccy + 'INR'];
              if (!ccyData) return null;
              return (
                <CurrencyCard
                  key={ccy}
                  ccy={ccy}
                  data={ccyData}
                  hedge={hedgeData}
                  liveRate={liveRate}
                />
              );
            })}
          </div>

          <InstrumentCards forwards={forwards} pcfc={pcfc} />

          <PerformanceSection token={token} />
        </>
      )}
    </div>
  );
}